import posthog from "posthog-js";
import { isPostHogEnabled, isPostHogInitialized } from "@features/shared/posthog";
import { withLocalFlagOverrides } from "./local-overrides";

/**
 * Feature-flags client backed by PostHog. The flags model talks ONLY to this
 * interface (the shape dates back to the Amplitude Experiment client it
 * replaced, so the model needed no changes when the provider switched):
 * - variant(key) returns { value, payload, key } and TRACKS an exposure
 *   ($feature_flag_called) — lazy-evaluated flags keep their
 *   "exposed population = eligible users" semantics.
 * - all() is an exposure-free snapshot (backend event enrichment, upsell config).
 */
export type FlagVariant = { value?: string; payload?: any; key?: string };

export interface FlagsClient {
    readonly provider: FlagsProviderName;
    fetch(): Promise<void>;
    variant(key: string): FlagVariant;
    all(): Record<string, FlagVariant>;
}

export type FlagsProviderName = "posthog";

const FETCH_TIMEOUT_MS = 10_000;

/**
 * PostHog variant keys allow only letters/digits/underscores/dashes, while
 * some legacy (Amplitude-era) variants contain spaces and apostrophes. The
 * PostHog flag is created with the sanitized key (left), and variant()
 * translates it back to the canonical historical value (right) — downstream
 * consumers, event properties and user-prop mirrors keep seeing the names the
 * accumulated analytics were built on.
 *
 * Add an entry here whenever a migrated flag needs renamed variants.
 */
const POSTHOG_VARIANT_ALIASES: Record<string, Record<string, string>> = {
    product_onboarding_variant: {
        annas_test3_challenge: "anna's test3 challenge",
        control_challenge: "control challenge"
    }
};

const toCanonicalValue = (flagKey: string, value: string): string =>
    POSTHOG_VARIANT_ALIASES[flagKey]?.[value] ?? value;

/**
 * PostHog flag values: true → "on" (boolean flag), string → variant key.
 * Exported for the $feature_flag_called → $exposure mirror, which must store
 * the same canonical names variant() serves.
 */
export const toVariantValue = (flagKey: string, value: unknown): string | undefined => {
    if (value === true) return "on";
    if (typeof value === "string" && value.length > 0) return toCanonicalValue(flagKey, value);
    return undefined;
};

const waitForPostHogInit = async (): Promise<boolean> => {
    const started = Date.now();
    while (!isPostHogInitialized()) {
        if (Date.now() - started > FETCH_TIMEOUT_MS) return false;
        await new Promise((resolve) => setTimeout(resolve, 100));
    }
    return true;
};

const createPostHogFlagsClient = (): FlagsClient | null => {
    if (!isPostHogEnabled()) return null;

    // Exposure-free snapshot for all(): kept in sync from onFeatureFlags
    // callbacks (their payload carries every variant without tracking).
    let snapshot: Record<string, FlagVariant> = {};
    let snapshotListenerAttached = false;

    const rebuildSnapshot = (variants: Record<string, string | boolean>) => {
        const next: Record<string, FlagVariant> = {};
        for (const [key, raw] of Object.entries(variants ?? {})) {
            const value = toVariantValue(key, raw);
            if (!value) continue;
            // getFeatureFlagPayload does NOT track an exposure (unlike getFeatureFlag)
            next[key] = { value, key: value, payload: posthog.getFeatureFlagPayload(key) ?? undefined };
        }
        snapshot = next;
    };

    const attachSnapshotListener = () => {
        if (snapshotListenerAttached) return;
        snapshotListenerAttached = true;
        posthog.onFeatureFlags((_flags, variants) => rebuildSnapshot(variants ?? {}));
    };

    return {
        provider: "posthog",
        // Forces a reload so refetch cycles (login, identity stitching) serve
        // variants for the CURRENT identity.
        fetch: () =>
            new Promise<void>((resolve) => {
                void (async () => {
                    const ready = await waitForPostHogInit();
                    if (!ready) {
                        resolve();
                        return;
                    }
                    attachSnapshotListener();

                    let settled = false;
                    const finish = () => {
                        if (settled) return;
                        settled = true;
                        clearTimeout(timer);
                        resolve();
                    };
                    const timer = setTimeout(finish, FETCH_TIMEOUT_MS);
                    const unsubscribe = posthog.onFeatureFlags((_flags, variants) => {
                        rebuildSnapshot(variants ?? {});
                        try {
                            unsubscribe?.();
                        } catch {}
                        finish();
                    });
                    try {
                        posthog.reloadFeatureFlags();
                    } catch {
                        finish();
                    }
                })();
            }),
        variant: (key) => {
            if (!isPostHogInitialized()) return {};
            try {
                // getFeatureFlag tracks $feature_flag_called (the exposure event)
                const value = toVariantValue(key, posthog.getFeatureFlag(key));
                if (!value) return {};
                return { value, key: value, payload: posthog.getFeatureFlagPayload(key) ?? undefined };
            } catch {
                return {};
            }
        },
        all: () => snapshot
    };
};

/**
 * Factory used by the flags model on app mount. Call client-side only.
 * Returns null when PostHog is not configured (NEXT_PUBLIC_POSTHOG_* absent) —
 * the model then releases its ready gates with default flag values.
 */
export const createFlagsClient = (): FlagsClient | null => withLocalFlagOverrides(createPostHogFlagsClient());
